import { ChevronRightIcon } from "@radix-ui/react-icons";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useGetLesson, useUpdateLesson } from "src/api/lms/lms";
import TiptapEditor from "src/components/ui/editor/tiptap-editor";
import { TextInput } from "src/components/ui/text-input";

type Props = {};

export default function Lesson({}: Props) {
  const { lessonId } = useParams();
  const { data: lesson } = useGetLesson(String(lessonId));
  const updateLessonMutation = useUpdateLesson();
  const [videoUrl, setVideoUrl] = useState("");
  useEffect(() => {
    if (lesson?.videoUrl) {
      setVideoUrl(lesson.videoUrl);
    }
  }, [lesson?.videoUrl]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Typography,
      Placeholder.configure({
        placeholder: "Details about the lesson..",
        showOnlyWhenEditable: false,
      }),
    ],
    content: lesson?.summary,
    onBlur: ({ editor }) => {
      if (lessonId && lesson?.summary.trim() !== editor.getHTML().trim()) {
        updateLessonMutation.mutate({
          _id: lessonId,
          summary: editor.getHTML(),
        });
      }
    },
    autofocus: true,
  });

  useEffect(() => {
    if (lesson?.summary) {
      editor?.commands.setContent(lesson.summary);
    }
  }, [lesson?.summary]);

  const [description, setDescription] = useState(lesson?.description);
  useEffect(() => {
    if (lesson?.description) {
      setDescription(lesson.description);
    }
  }, [lesson?.description]);

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="flex h-12 shrink-0 items-center border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          <RouterLink to={`/lms`}>
            <div className="text-gray-800">{"LMS"}</div>
          </RouterLink>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <RouterLink to={`/lms/lessons`}>
            <div className="text-gray-800">{"Lessons"}</div>
          </RouterLink>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{lesson?.title}</div>
        </div>
      </div>
      <div className="flex-1 px-8 py-24">
        <div className="mx-auto flex max-w-2xl flex-col space-y-6">
          <div className="space-y-2">
            <div className="text-2xl font-medium text-gray-800">
              {lesson?.title}
            </div>
            <div className="max-w-2xl text-base font-light text-gray-600">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => {
                  if (lesson?._id) {
                    updateLessonMutation.mutate({
                      _id: lesson?._id,
                      description: description,
                    });
                  }
                }}
                placeholder="Description..."
                className="w-full resize-none border-none bg-transparent p-0 focus:border-none focus:outline-none focus:ring-0"
              />
            </div>
          </div>
          <div className="space-y-2 pt-4">
            <TextInput
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onBlur={() => {
                if (lesson?._id) {
                  updateLessonMutation.mutate({
                    _id: lesson?._id,
                    videoUrl: videoUrl,
                  });
                }
              }}
            />
            <iframe
              src={videoUrl}
              className="h-[400px] w-full rounded-xl"
            ></iframe>
          </div>
          <div className="mt-10 rounded-md border p-6 shadow">
            <TiptapEditor editor={editor} />
          </div>
        </div>
      </div>
    </div>
  );
}
